/**
 * FLOW COMPILER — turns the canvas drawing into the step list the call runtime executes.
 *
 * THE CONTRACT. `playbooks.graph` is what the editor draws. `playbooks.steps` is what
 * answers the phone. This module is the only bridge, and it runs in one direction only:
 * graph → steps, never back. A flow that was not built on the canvas keeps its steps and is
 * marked `graph_stale`, because silently drawing an empty canvas over a working flow and
 * inviting somebody to press Save is how a call flow disappears at 2 AM.
 *
 * WHY COMPILE AT ALL. The runtime already has a validated vocabulary and a validator that
 * enforces doctrine 1 (nothing connects a caller to a person). Making the canvas the
 * runtime format would put every drag one bug away from a live call. Compiling means the
 * worst a broken drawing can do is refuse to save.
 *
 * THE HARD PART is that a canvas is a GRAPH and the runtime takes a TREE: an ordered list
 * with nested branch cases. Compilation is therefore a traversal that has to decide what to
 * do about the two shapes a graph allows and a tree does not — a node reached from more
 * than one place, and a cycle. Both are handled explicitly below rather than by crashing or,
 * worse, by emitting something plausible.
 */
import type { PlaybookStep } from './contract.ts';

// ------------------------------------------------------------------------ the graph shape

export type FlowNode = {
  id: string;
  type: FlowNodeType;
  position: { x: number; y: number };
  data: Record<string, unknown>;
};

export type FlowEdge = {
  id: string;
  source: string;
  target: string;
  /** Which outlet of the source it leaves from. Branch nodes have one per case. */
  sourceHandle?: string | null;
  label?: string | null;
};

export type FlowGraph = { nodes: FlowNode[]; edges: FlowEdge[] };

/**
 * The palette. Every type maps to a step the runtime already understands — there is
 * deliberately no node whose behaviour has to be invented at execution time.
 *
 * `arm` is the exception and the reason it is listed last: it does not speak to the caller,
 * it changes what EVERY later caller hears. It compiles to a tool step against a validated
 * situation key, and the UI puts a confirm on any button that contains one.
 */
export type FlowNodeType =
  | 'entry' | 'say' | 'ask' | 'branch' | 'lookup' | 'do'
  | 'page' | 'message' | 'end' | 'arm';

export type CompileIssue = { nodeId: string | null; message: string };
export type CompileResult =
  | { ok: true; steps: PlaybookStep[]; warnings: CompileIssue[] }
  | { ok: false; errors: CompileIssue[]; warnings: CompileIssue[] };

const MAX_STEPS = 60;

/** Read a string field without trusting the canvas to have typed it. */
function str(data: Record<string, unknown>, key: string): string {
  const v = data[key];
  return typeof v === 'string' ? v.trim() : '';
}

export function compileFlow(graph: FlowGraph): CompileResult {
  const errors: CompileIssue[] = [];
  const warnings: CompileIssue[] = [];

  const nodes = new Map(graph.nodes.map((n) => [n.id, n]));
  const out = new Map<string, FlowEdge[]>();
  for (const e of graph.edges) {
    if (!nodes.has(e.source)) { errors.push({ nodeId: null, message: `An edge starts from a node that is not on the canvas.` }); continue; }
    if (!nodes.has(e.target)) { errors.push({ nodeId: e.source, message: `A connection points at a node that is not on the canvas.` }); continue; }
    const list = out.get(e.source) ?? [];
    list.push(e);
    out.set(e.source, list);
  }

  const entries = graph.nodes.filter((n) => n.type === 'entry');
  if (entries.length === 0) errors.push({ nodeId: null, message: 'This flow has no starting point. Add a Call arrives node.' });
  if (entries.length > 1) errors.push({ nodeId: null, message: 'This flow has more than one starting point. A call can only begin in one place.' });

  // Anything the caller can never reach is almost always a half-finished edit, and it is
  // worth saying so — but it is not a reason to refuse a save mid-thought.
  if (entries.length === 1) {
    const seen = new Set<string>();
    const stack = [entries[0]!.id];
    while (stack.length) {
      const id = stack.pop()!;
      if (seen.has(id)) continue;
      seen.add(id);
      for (const e of out.get(id) ?? []) stack.push(e.target);
    }
    for (const n of graph.nodes) {
      if (!seen.has(n.id) && n.type !== 'entry') {
        warnings.push({ nodeId: n.id, message: 'Nothing leads here, so a caller will never reach it.' });
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors, warnings };

  const steps: PlaybookStep[] = [];
  /**
   * `onPath` catches a genuine loop — a caller trapped repeating a question forever, which
   * is the single worst thing this editor could ship. `emitted` catches the merely
   * diamond-shaped: two branches that rejoin. A tree cannot express the rejoin, so the
   * shared tail is emitted once per path and we say so, rather than silently dropping the
   * second copy and changing what the caller hears.
   */
  const onPath = new Set<string>();
  const emitted = new Set<string>();
  let truncated = false;

  const walk = (id: string): void => {
    if (steps.length >= MAX_STEPS) { truncated = true; return; }
    const node = nodes.get(id);
    if (!node) return;

    if (onPath.has(id)) {
      errors.push({ nodeId: id, message: 'This connection loops back on itself, so a caller would never get out of it.' });
      return;
    }
    if (emitted.has(id) && node.type !== 'end') {
      warnings.push({ nodeId: id, message: 'More than one path reaches this step, so it is repeated in each. Consider splitting the flow.' });
    }
    onPath.add(id);
    emitted.add(id);

    const next = out.get(id) ?? [];
    const first = (): void => { if (next[0]) walk(next[0].target); };

    switch (node.type) {
      case 'entry':
        first();
        break;

      case 'say': {
        const text = str(node.data, 'text');
        if (!text) errors.push({ nodeId: id, message: 'This step speaks to the caller but has nothing to say.' });
        else steps.push({ type: 'say', text });
        first();
        break;
      }

      case 'ask': {
        const text = str(node.data, 'text');
        const key = str(node.data, 'key');
        if (!text) errors.push({ nodeId: id, message: 'This step asks the caller something but has no question.' });
        if (!key) errors.push({ nodeId: id, message: 'This question has no name, so nothing later can branch on the answer.' });
        if (text && key) steps.push({ type: 'ask', text, note: key });
        first();
        break;
      }

      case 'branch': {
        const on = str(node.data, 'on');
        if (!on) errors.push({ nodeId: id, message: 'This branch does not say what it is deciding on.' });
        if (next.length === 0) errors.push({ nodeId: id, message: 'This branch has no outcomes connected.' });
        // The runtime's branch carries its cases inline; each outlet is one case, and the
        // handle id IS the case value, which is why the editor must not let it be blank.
        for (const e of next) {
          const caseValue = (e.sourceHandle ?? e.label ?? '').trim();
          if (!caseValue) {
            errors.push({ nodeId: id, message: 'One outcome of this branch is unlabelled, so the assistant cannot tell which way to go.' });
            continue;
          }
          steps.push({ type: 'branch', note: `${on}=${caseValue}` });
          walk(e.target);
        }
        break;
      }

      case 'lookup': {
        const tool = str(node.data, 'tool');
        if (!tool) errors.push({ nodeId: id, message: 'This lookup does not say what to look up.' });
        else steps.push({ type: 'tool', tool, note: 'read-only' });
        first();
        break;
      }

      case 'do': {
        // Doctrine 2. Anything that changes the world outside this call is a PROPOSAL; the
        // runtime gates it. The compiler records the intent and never the authority.
        const tool = str(node.data, 'tool');
        if (!tool) errors.push({ nodeId: id, message: 'This action does not say what to do.' });
        else steps.push({ type: 'tool', tool, note: 'gated: proposed for approval' });
        first();
        break;
      }

      case 'page':
        // Doctrine 1 lives here: this pages a technician, it never bridges the caller to
        // one. There is no node type that could, and the runtime validator rejects one.
        steps.push({ type: 'take_message', note: 'voicemail first, then page on-call' });
        first();
        break;

      case 'message':
        steps.push({ type: 'take_message', note: str(node.data, 'category') || 'customer' });
        first();
        break;

      case 'arm': {
        const key = str(node.data, 'situation');
        if (!key) errors.push({ nodeId: id, message: 'This step changes the line but does not say which mode to switch on.' });
        else steps.push({ type: 'tool', tool: 'arm_situation', note: key });
        first();
        break;
      }

      case 'end':
        steps.push({ type: 'end' });
        break;

      default:
        errors.push({ nodeId: id, message: 'This node type is not something the assistant can run.' });
    }

    onPath.delete(id);
  };

  walk(entries[0]!.id);

  if (truncated) {
    errors.push({ nodeId: null, message: `This flow compiles to more than ${MAX_STEPS} steps. Split it into two flows with different triggers.` });
  }
  // A flow that can run off the end says nothing about hanging up, and the runtime would
  // improvise. It must not improvise.
  if (steps.length > 0 && steps[steps.length - 1]?.type !== 'end') {
    warnings.push({ nodeId: null, message: 'One path does not finish with an End step, so the assistant decides how the call ends.' });
  }
  if (steps.length === 0) errors.push({ nodeId: null, message: 'This flow does not do anything yet.' });

  if (errors.length > 0) return { ok: false, errors, warnings };
  return { ok: true, steps, warnings };
}
